import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';

type RecentActivity = {
  id: string;
  session_id: string | null;
  created_at: string;
  event: string;
  first_name: string | null;
  email: string | null;
  industry: string | null;
  demo_mode: boolean;
  version: string | null;
};

type DashboardData = {
  in_queue: number;
  pending_edits: number;
  delivered_this_month: number;
  avg_synthesis_ms: number | null;
  recent_activity: RecentActivity[];
  pipeline: { week_start: string; in_progress: number; submitted: number; delivered: number; rejected: number }[];
};

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await api.dashboard();
        if (!cancelled) setData(result);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Could not load dashboard.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (error) return <p className="font-serif text-accent">{error}</p>;
  if (!data) return <p className="font-mono text-[11px] uppercase tracking-wider text-ink-light">Loading…</p>;

  return (
    <div className="max-w-5xl">
      <h1 className="font-display text-3xl font-medium text-ink mb-8">Dashboard</h1>

      {/* Hero stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <StatCard
          label="In queue"
          value={data.in_queue}
          to="/queue?status=submitted"
          tone={data.in_queue > 0 ? 'accent' : 'neutral'}
        />
        <StatCard label="Pending edits" value={data.pending_edits} to="/queue?status=drafts" tone="neutral" />
        <StatCard label="Delivered this month" value={data.delivered_this_month} to="/delivered" tone="neutral" />
        <StatCard
          label="Avg synthesis time"
          value={data.avg_synthesis_ms != null ? `${(data.avg_synthesis_ms / 1000).toFixed(1)}s` : '—'}
          subtle
        />
      </div>

      {/* Recent activity */}
      <section className="mb-12">
        <h2 className="font-display text-xl font-medium text-ink mb-4">Recent activity</h2>
        {data.recent_activity.length === 0 ? (
          <p className="font-serif text-ink-light italic">No activity yet.</p>
        ) : (
          <ul className="divide-y divide-rule border border-rule bg-paper">
            {data.recent_activity.map((a) => (
              <li key={a.id} className="px-4 py-3 hover:bg-cream-dark transition-colors">
                {a.session_id ? (
                  <Link to={`/review/${a.session_id}`} className="block font-serif text-sm text-ink no-underline">
                    <ActivityRow a={a} />
                  </Link>
                ) : (
                  <ActivityRow a={a} />
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Pipeline snapshot */}
      <section>
        <h2 className="font-display text-xl font-medium text-ink mb-4">Pipeline (last 8 weeks)</h2>
        {data.pipeline.length === 0 ? (
          <p className="font-serif text-ink-light italic">No data in window.</p>
        ) : (
          <div className="overflow-x-auto border border-rule bg-paper">
            <table className="w-full font-mono text-[12px]">
              <thead>
                <tr className="bg-cream-dark text-ink-light uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Week of</th>
                  <th className="px-3 py-2 text-right">In progress</th>
                  <th className="px-3 py-2 text-right">Submitted</th>
                  <th className="px-3 py-2 text-right">Delivered</th>
                  <th className="px-3 py-2 text-right">Rejected</th>
                </tr>
              </thead>
              <tbody>
                {data.pipeline.map((row) => (
                  <tr key={row.week_start} className="border-t border-rule">
                    <td className="px-3 py-2 text-ink">{row.week_start}</td>
                    <td className="px-3 py-2 text-right text-ink-soft">{row.in_progress}</td>
                    <td className="px-3 py-2 text-right text-ink-soft">{row.submitted}</td>
                    <td className="px-3 py-2 text-right text-ink-soft">{row.delivered}</td>
                    <td className="px-3 py-2 text-right text-ink-soft">{row.rejected}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({
  label,
  value,
  to,
  tone = 'neutral',
  subtle = false,
}: { label: string; value: string | number; to?: string; tone?: 'accent' | 'neutral'; subtle?: boolean }) {
  const inner = (
    <div
      className={`px-4 py-5 border bg-paper transition-colors ${
        to ? 'cursor-pointer hover:border-accent' : ''
      } ${tone === 'accent' && (typeof value === 'number' ? value > 0 : false) ? 'border-accent' : 'border-rule'}`}
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-2">{label}</div>
      <div className={`font-display text-3xl font-medium ${subtle ? 'text-ink-soft' : 'text-ink'}`}>
        {value}
      </div>
    </div>
  );
  return to ? <Link to={to} className="no-underline">{inner}</Link> : inner;
}

function ActivityRow({ a }: { a: RecentActivity }) {
  const ts = new Date(a.created_at);
  const ago = relativeTime(ts);
  const name = a.first_name || (a.email ? a.email.split('@')[0] : '(no name)');
  const eventLabel = formatEvent(a.event);
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <span className="font-mono text-[11px] uppercase tracking-wider text-ink-light flex-shrink-0">{ago}</span>
        <span className="text-ink-soft">·</span>
        <span className="font-mono text-[11px] uppercase tracking-wider text-ink truncate">{eventLabel}</span>
        {a.demo_mode && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-gold-warm bg-cream-dark px-2 py-0.5">demo</span>
        )}
        {a.version === 'v1' && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light bg-cream-dark px-2 py-0.5">v1</span>
        )}
      </div>
      <div className="text-ink-soft text-sm truncate">
        {name}
        {a.industry && <span className="text-ink-light"> · {industryShort(a.industry)}</span>}
      </div>
    </div>
  );
}

function formatEvent(e: string): string {
  return e.replace(/_/g, ' ');
}

function industryShort(key: string): string {
  const map: Record<string, string> = {
    professional_services: 'professional services',
    real_estate: 'real estate',
    health_wellness: 'health & wellness',
    ecommerce_retail: 'e-commerce',
    trades_manufacturing: 'trades',
    saas_software: 'SaaS',
    other: 'other',
  };
  return map[key] || key;
}

function relativeTime(d: Date): string {
  const ms = Date.now() - d.getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  if (days < 14) return `${days}d ago`;
  return d.toISOString().slice(0, 10);
}
